/**
 * Created by andrey on 13.10.2023
 */

const PaymentsManager = function () {
    this.pending = [];
    this.consumed = [];
    this.logged = [];
    this.load();

    this.unconsumed = {};

    this.productsConfigsCallbacks = [];
    this.productsConfig = undefined;
    this.productsConfigLoading = false;

    this.restoreThrottled = connector.utils.timeredThrottle(10000, this.restore.bind(this));
    this.tryLoadProductsThrottled = connector.utils.timeredThrottle(10000, this.tryLoadProducts.bind(this));

    connector.timeouts.setInterval(() => this.restoreThrottled(), connector.utils.parseInterval(connector.config.debugMode ? "1 minute" : "10 minutes"));

    connector.payments.on("changeStatus", (status) => {
        if (status === connector.STATUS_CONNECTED) {
            this.armLoadProducts();
            this.tryLoadProductsThrottled();

            this.restoreThrottled();

            ["30 seconds", "1 minute", "5 minutes"].forEach((timeout) => {
                connector.timeouts.setTimeout(() => this.restoreThrottled(), connector.utils.parseInterval(timeout));
            });

            ["20 seconds", "40 seconds", "60 seconds", "90 seconds"].forEach((timeout) => {
                connector.timeouts.setTimeout(() => this.tryLoadProductsThrottled(), connector.utils.parseInterval(timeout));
            });
        }
    });

    connector.payments.on("productsFailed", () => this.armLoadProducts());
    connector.payments.on("pluginChanged", () => this.armLoadProducts());

    // a tick skipped while the page was hidden or an ad played is asked for on return
    connector.platform.on("resume", () => {
        this.tryLoadProductsThrottled();
        this.restoreThrottled();
    });

    if (connector.platform.oneOf(connector.MSSTART, connector.EPICGAMES)) {
        connector.social.on("login", () => this.tryLoadProductsThrottled());
    }

    connector._countryManager.on("changed", () => {
        this.whenIdle(() => {
            connector.payments._refreshPlugin();
        });
    });
};

PaymentsManager.prototype.loadProductsConfig = function (callback) {
    if (this.productsConfig) {
        callback(this.productsConfig);
        return;
    }

    this.productsConfigsCallbacks.push(callback);

    if (this.productsConfigLoading) {
        return;
    }

    this.productsConfigLoading = true;

    const onProductsLoaded = function (productsConfig) {
        this.productsConfigLoading = false;

        this.productsConfig = productsConfig;

        const callbacks = this.productsConfigsCallbacks;

        this.productsConfigsCallbacks = [];

        callbacks.forEach((callback) => {
            callback(productsConfig);
        });
    }.bind(this);

    if (connector.utils.getRequestParameters(window.location.hash).stopProductsLoadingDebug) {
        onProductsLoaded(undefined);
        return;
    }

    const products = window.CONNECTOR_PRODUCTS;

    let url;

    if (products && products.remote && products.remote.startsWith("remoteconfig://") && connector.config.remoteConfigURL) {
        url = products.remote;
    } else if (connector.config.payApiURL) {
        url = `${connector.config.payApiURL}/products.json`;
    }

    if (url) {
        connector._loadConfig(
            url,
            connector.utils.waitNoMore(BasePlugin.CONNECT_TIMEOUT, onProductsLoaded)
        );
    } else {
        onProductsLoaded(undefined);
    }
};

PaymentsManager.prototype.getLocalizedCatalog = function () {
    return connector.payments.getPlugin().listProducts().map(this.localizeProduct.bind(this));
};

PaymentsManager.prototype.whenIdle = function (callback) {
    const plugin = connector.payments.getPlugin();

    if (!plugin) {
        callback();
        return;
    }

    if (plugin.isIdle()) {
        callback();
    } else {
        plugin.bus.once("paymentClosed", callback);
    }
};

PaymentsManager.prototype.stop = function () {
    this.stopLoadProductsPlayer();
    this.stopRestorePlayer();
    this.stopPurchasePlayer();
};

PaymentsManager.prototype.stopRestorePlayer = function () {
    if (this.restorePlayer) {
        this.restorePlayer.stop();
        delete this.restorePlayer;
    }
};

// a store call while the page is hidden or an ad plays has nobody to show its result to, and its late answer into a
// minimized webview is the one the native hosts report as an exception; the resume that follows asks again
PaymentsManager.prototype.isPageBusy = function () {
    return connector.platform.isPageHidden() || connector.ads.isRewardedPlaying || connector.ads.isInterstitialPlaying;
};

// the periodic restore: the store is asked for its unconsumed purchases
PaymentsManager.prototype.restore = function (callback) {
    if (!connector.payments.isConnected() || !connector.payments.getPlugin().isRestoreAvailable() || this.isPageBusy()) {
        callback && callback();
        return;
    }

    this._restoreInner((f, stop) => {
        connector.payments.getPlugin().restore((code, restorePurchase) => {
            if (code === connector.CODE_SUCCEED) {
                f(restorePurchase);
            } else {
                stop();
            }
        });
    }, callback);
};

// a purchase in hand: delivered through the restore path without asking the store again
PaymentsManager.prototype.restorePurchase = function (purchase) {
    this._restoreInner((f) => f(purchase));
};

// a purchase answered after it was closed (a late store answer, or our validate after the overlay closed): through the restore
// path at once where the store's id is stable - an unstable one, new per call, would grant the unit again after a restore consumed
// it - and by the store's restore in 15 s, 1 and 5 minutes: a busy restore path (a purchase, a restore, an unconsumed
// receipt) skips a try, and without the later ones the purchase would wait for the 10-minute tick
PaymentsManager.prototype.restoreLatePurchase = function (purchase) {
    if (connector.payments.getPlugin().isPaymentIdStable()) {
        connector.timeouts.setTimeout(() => this.restorePurchase(purchase), 0);
    }
    this.restoreSoon();
};

PaymentsManager.prototype.restoreSoon = function () {
    ["15 seconds", "1 minute", "5 minutes"].forEach((timeout) => {
        connector.timeouts.setTimeout(() => this.restoreThrottled(), connector.utils.parseInterval(timeout));
    });
};

PaymentsManager.prototype._restoreInner = function (fetchPurchase, callback) {
    callback = callback || function () {};

    if (!connector.payments.isConnected() || this.isPurchaseRunning() || this.isRestoreRunning() || this.hasUnconsumed()) {
        callback();
        return;
    }

    let purchase, restorePurchase;

    const player = this.restorePlayer = new ConnectorActionPlayer({
        actionTimeout: connector.utils.parseInterval("1 minute"),
        actions: [
            function (f, stop) {
                fetchPurchase((fetched) => {
                    fetched.restore = true;
                    purchase = fetched;

                    console.log("[connector] Restore purchase", JSON.stringify({ ...fetched, payload: undefined }));

                    f();
                }, stop);
            },

            function (f, stop) {
                if (this.isConsumed(purchase.paymentId)) {
                    this.consume(purchase);
                    stop();
                    return;
                }

                connector.payments.getPlugin().validate(purchase, (code) => {
                    if (code === connector.CODE_SUCCEED) {
                        f();
                    } else {
                        if (code === connector.CODE_FAILED) {
                            this.consume(purchase);
                        }
                        stop();
                    }
                });
            }.bind(this),

            function (f) {
                const context = this.findPendingPurchaseContext(purchase);

                restorePurchase = { ...context, ...purchase, pendingMatch: this.describePendingMatch(purchase) };

                f();
            }.bind(this)
        ]
    });

    player.onSuccess(() => {
        this.markUnconsumed(restorePurchase);

        connector.payments.getPlugin().bus.trigger("restore", restorePurchase);

        this.logPurchase(restorePurchase);

        callback(restorePurchase);
    });

    player.onFailure(() => {
        callback();
    });

    player.play();
};

PaymentsManager.prototype.load = function () {
    const info = connector.dataLoader.load(SimpleDataLoader.TYPES.PAYMENTS);
    if (info) {
        this.pending = this.freshPending(info.pending || []);
        this.consumed = (info.consumed || []).slice();
        this.logged = (info.logged || []).slice();

        this.save(true);
    }
};

PaymentsManager.prototype.save = function (fromServer) {
    connector.dataLoader.save(SimpleDataLoader.TYPES.PAYMENTS, this.getInfo());

    if (!fromServer && connector.synchronizer) {
        connector.synchronizer.addSendTask("payments");
    }
};

PaymentsManager.prototype.getInfo = function () {
    return {
        pending: this.pending.slice(-PaymentsManager.PENDING_STORAGE_LIMIT),
        consumed: this.consumed.slice(-PaymentsManager.CONSUMED_STORAGE_LIMIT),
        logged: this.logged.slice(-PaymentsManager.LOGGED_STORAGE_LIMIT)
    };
};

PaymentsManager.prototype.updateInfo = function (serverInfo) {
    serverInfo = serverInfo || {};

    this.pending = this.freshPending(serverInfo.pending || []);
    this.consumed = (serverInfo.consumed || []).slice();
    this.logged = (serverInfo.logged || []).slice();

    this.save(true);
};

// an entry older than PENDING_LIFETIME is dropped when the list is read; an entry of a build before the time stamp gets the time
// of its first read and load saves it locally, the server copy gets it with the next sent save - until then a pull stamps it anew
PaymentsManager.prototype.freshPending = function (pending) {
    return pending.map((entry) => ({ time: Date.now(), ...entry })).filter((entry) => entry.time > Date.now() - PaymentsManager.PENDING_LIFETIME);
};

PaymentsManager.prototype.savePendingPurchaseContext = function (productId, context) {
    const entry = { productId, context, time: Date.now() };

    this.pending.push(entry);
    this.save();

    return entry;
};

PaymentsManager.prototype.attachPendingPaymentId = function (entry, purchase) {
    const paymentId = purchase && purchase.paymentId;

    if (paymentId && !RestPayments.IsTmpPaymentId(paymentId) && entry.paymentId !== paymentId) {
        entry.paymentId = paymentId;
        this.save();
    }
};

PaymentsManager.prototype.indexOfPendingPurchaseContext = function (purchase) {
    const paymentId = RestPayments.IsTmpPaymentId(purchase.paymentId) ? undefined : purchase.paymentId;
    let index = -1;

    for (let i = this.pending.length - 1; i >= 0; --i) {
        const entry = this.pending[i];

        if (paymentId && entry.paymentId === paymentId) {
            return i;
        }

        if (index === -1 && entry.productId === purchase.productId && (!entry.paymentId || !paymentId || !connector.payments.getPlugin().isPaymentIdStable())) {
            index = i;
        }
    }

    return index;
};

// how a receipt found its pending context, for product_restore_received: by - id (exact payment id), product (newest same-product entry
// without an id, or with any id where the store's id is unstable, a new id per call) or none; choices - the same-product entries it could
// have taken (shared price tiers), newest first; unconsumed - the newest of them whose payment id is not consumed, when it differs
PaymentsManager.prototype.describePendingMatch = function (purchase) {
    const index = this.indexOfPendingPurchaseContext(purchase);
    const entry = this.pending[index];
    const paymentId = RestPayments.IsTmpPaymentId(purchase.paymentId) ? undefined : purchase.paymentId;

    const describe = (choice) => ({
        type: choice.context && choice.context.type,
        paymentId: choice.paymentId,
        age: choice.time ? Math.round((Date.now() - choice.time) / 60000) : undefined
    });

    const choices = this.pending.filter((choice) => choice.productId === purchase.productId).reverse();
    const unconsumed = choices.find((choice) => !this.isConsumed(choice.paymentId));

    let by = "none";
    if (entry) {
        by = paymentId && entry.paymentId === paymentId ? "id" : "product";
    }

    return {
        by,
        entry: entry && describe(entry),
        choices: choices.length,
        choiceTypes: choices.map((choice) => (choice.context && choice.context.type) || "no_context"),
        unconsumed: unconsumed && unconsumed !== entry ? describe(unconsumed) : undefined
    };
};

PaymentsManager.prototype.removePendingPurchaseContext = function (purchase) {
    const index = this.indexOfPendingPurchaseContext(purchase);
    if (index !== -1) {
        this.pending.splice(index, 1);
        this.save();
    }
};

PaymentsManager.prototype.findPendingPurchaseContext = function (purchase) {
    const index = this.indexOfPendingPurchaseContext(purchase);
    if (index !== -1) {
        return this.pending[index];
    }
};

PaymentsManager.prototype.getPaymentContext = function (purchase) {
    if (purchase.context) {
        return purchase.context;
    }

    const pending = this.findPendingPurchaseContext(purchase);
    return pending && pending.context;
};

PaymentsManager.prototype.isConsumed = function (paymentId) {
    return paymentId && this.consumed.includes(paymentId);
};

PaymentsManager.prototype.consume = function (purchase, callback) {
    callback = callback || function () {};

    // _consumeInner drops the flag, and a restored receipt asks for the next pending one
    const restore = purchase.restore;

    this.markUnconsumed(purchase);

    this._consumeInner(purchase, (code) => {
        delete this.unconsumed[purchase.paymentId];

        const plugin = connector.payments.getPlugin();

        const product = connector.payments.getProduct(purchase.productId);

        const result = {
            purchase,
            product
        };

        if (code) {
            plugin.bus.trigger("error:consume", code);

            callback();
        } else {
            plugin.bus.trigger("consume", result);

            callback(result);
        }

        // asked only now: a restore during the consume reads the store before the unit is gone and hands it back again
        if (restore) {
            this.restoreThrottled();
        }
    });
};

// a receipt handed to the game, or a consume in flight: the store still holds the unit, so a restore now returns it
// again - under a new id where the store mints one per call (Microsoft) - and the game grants it twice. Session-only,
// a restart asks the store afresh; the ceiling frees the restore from a receipt the game never consumes
PaymentsManager.prototype.markUnconsumed = function (purchase) {
    if (purchase.paymentId) {
        this.unconsumed[purchase.paymentId] = Date.now();
    }
};

PaymentsManager.prototype.hasUnconsumed = function () {
    const now = Date.now();

    Object.keys(this.unconsumed).forEach((paymentId) => {
        if (now - this.unconsumed[paymentId] > PaymentsManager.CONSUME_TIMEOUT) {
            delete this.unconsumed[paymentId];
        }
    });

    return Object.keys(this.unconsumed).length > 0;
};

PaymentsManager.prototype._consumeInner = function (purchase, callback) {
    purchase.state = purchase.restore ? 4 : 2;
    delete purchase.restore;

    callback = connector.utils.once(callback);

    if (purchase.paymentId && !this.consumed.includes(purchase.paymentId)) {
        this.removePendingPurchaseContext(purchase);

        this.consumed.push(purchase.paymentId);
        this.save();
    }

    const product = connector.payments.getProduct(purchase.productId);
    if (product.type === "nonConsumable" && connector.payments.oneOf(connector.SAMSUNG)) {
        callback(ERRORS.CONSUME.UNKNOWN);
    } else {
        connector.payments.getPlugin().consume(purchase, callback);
    }

    if (product.type !== "subscription" && !(connector.payments.getPlugin() instanceof RestPayments)) {
        const path = `/payments/add/${connector.info.source}/${encodeURIComponent(connector.platform.getUserID())}`;

        if (connector.payments.oneOf(connector.IAP)) {
            delete purchase.payload.receipt;
        }

        if (connector.config.payApiURL && !connector.config.norest) {
            ConnectorRestClient.post(path, {
                purchase,
                version: 2
            }, () => {}, (error) => {
                const plugin = connector.payments.getPlugin();
                plugin.bus.trigger("stream:error", `Error adding payments ${error.status} ${error.errorMessage} ${JSON.stringify(purchase)}`);
            });
        }
    }
};

PaymentsManager.prototype.localizeProduct = function (product) {
    if (!product) {
        return product;
    }

    const localizedProduct = connector.utils.clone(product, true);

    localizedProduct.title = localizedProduct.title[connector.info.language] !== undefined ? localizedProduct.title[connector.info.language] : localizedProduct.title.en;
    localizedProduct.description = localizedProduct.description[connector.info.language] !== undefined ? localizedProduct.description[connector.info.language] : localizedProduct.description.en;

    return localizedProduct;
};

PaymentsManager.prototype.getLocalizedProduct = function (productIdOrTag) {
    const product = connector.payments.getPlugin().getProduct(productIdOrTag);

    return this.localizeProduct(product);
};

PaymentsManager.prototype.isLoginRequired = function () {
    if (connector.payments.oneOf(connector.XSOLLA) && connector.platform.oneOf(connector.CRAZY)) {
        return true;
    }

    return !connector.payments.oneOf(
        connector.MICROSOFT,
        connector.PLINGA,
        connector.IAP,
        connector.RUSTORE,
        connector.ONESTORE,
        connector.YANDEX,
        connector.TEST,
        connector.YOOKASSA,
        connector.ROBOKASSA,
        connector.XSOLLA
    );
};

PaymentsManager.prototype.purchase = function (options, callback) {
    options = options || {};

    this._purchaseInner(options, (code, result, raw) => {
        const plugin = connector.payments.getPlugin();

        if (code) {
            plugin.bus.trigger("error:purchase", code, options);

            const product = connector.payments.getProduct(options.id || options.tag) || {};
            const usd = product.priceUSD !== undefined ? this.calcRealUSDPrice(product) : undefined;
            const params = {
                productId: product.itemId,
                textPrice: product.textPrice,
                method: plugin.pluginName,
                error: raw && raw.error !== undefined ? String(raw.error) : undefined,
                // raw: the store's own message; usd: the price in dollars next to the shown textPrice
                details: {
                    raw: raw && raw.message ? connector.utils.stringify(raw.message).substring(0, 200) : undefined,
                    usd: usd !== undefined && !Number.isNaN(usd) ? usd : undefined
                }
            };

            if (code === ERRORS.PURCHASE.CANCEL) {
                connector.metrics.logEvent(connector.EVENTS.STATS.PAYMENTS_CANCEL, params);
            } else {
                connector.metrics.logEvent(connector.EVENTS.STATS.PAYMENTS_ERROR + code, params);

                // the store can still charge after it reported a failure (OK: the dialog answers an error while SBP or the bank app
                // finishes the payment): the receipt waits for us, a restore in seconds delivers it instead of the 10-minute tick
                if (plugin.canDeliverLate()) {
                    this.restoreSoon();
                }
            }
        } else {
            this.markUnconsumed(result.purchase);

            plugin.bus.trigger("purchase", result);

            this.logPurchase(result.purchase);
        }

        callback(result);
    });
};

PaymentsManager.prototype.logPurchase = function (purchase) {
    const paymentId = purchase.paymentId;

    if (paymentId && this.logged.includes(paymentId)) {
        return;
    }

    const product = connector.payments.getPlugin().getProduct(purchase.productId);

    if (!connector.config.manualLogPayments) {
        connector.metrics.logEvent(connector.EVENTS.STATS.PAYMENTS, {
            productId: product.itemId,
            price: purchase.realPrice,
            textPrice: `${purchase.price} ${purchase.currency}`,
            paymentId: purchase.paymentId,
            episode: -1,
            level: -1
        });
    }

    connector._predictionManager.processPrediction(connector.EVENTS.PREDICTIONS.PAYMENTS, purchase.realPrice);
    connector.metrics.logEvent(connector.EVENTS.STATS.PURCHASE);

    let price = purchase.price;
    let currency = purchase.currency;
    if (connector.payments.isVirtualCurrency()) {
        price = purchase.realPrice;
        currency = "USD";
    }

    connector.conversions.trackPurchase(product, purchase, {
        transactionId: purchase.paymentId,
        price,
        currency
    });

    connector.conversions.trackEvent(connector.EVENTS.CONVERSIONS.PAYMENT, {
        value: purchase.realPrice,
        nickname: "d180a_payments,d7a_payments,day7_payment,d14a_payments,d30a_payments"
    });

    if (paymentId) {
        this.logged.push(paymentId);
        this.save();
    }
};

// What one nominal USD of catalogue price is really worth when charged in a virtual currency:
// how many units we charge per dollar, times what a unit is worth in rubles, over the ruble rate.
// Both inputs live in exchangerates.js, so repricing a platform updates reporting with it.
PaymentsManager.prototype.getVirtualCurrencyUSDCoef = function (currency) {
    const rate = connector.VIRTUAL_EXCHANGE_RATES[currency];
    const unitValue = connector.VIRTUAL_CURRENCY_UNIT_VALUE[currency];

    if (!rate || !unitValue) {
        return 1;
    }

    const unitUSD = unitValue.usd !== undefined ? unitValue.usd : unitValue.rub / connector.EXCHANGE_RATES.RUB;

    return rate * unitUSD;
};

PaymentsManager.prototype.calcRealUSDPrice = function (product) {
    const currency = product.currency;
    const price = product.price;
    const priceUSD = product.priceUSD;

    if (connector.payments.isVirtualCurrency()) {
        return Math.round(priceUSD * this.getVirtualCurrencyUSDCoef(currency) * 100) / 100;
    }

    const exchangeRate = connector.EXCHANGE_RATES[currency];

    if (!exchangeRate || exchangeRate === 1 || price === priceUSD) {
        return priceUSD;
    }

    return Math.round(price / exchangeRate * 100) / 100;
};

PaymentsManager.prototype._purchaseInner = function (options, callback) {
    options = options || {};

    callback = connector.utils.once(callback);

    const product = connector.payments.getProduct(options.id || options.tag);

    if (!product || !product.productId) {
        callback(ERRORS.PURCHASE.PRODUCT_NOT_FOUND);
        return;
    }

    let purchase;
    let pendingEntry;

    if (this.isPurchaseRunning()) {
        callback(ERRORS.PURCHASE.ALREADY_OPEN);
        return;
    }

    if (!connector.payments.isConnected()) {
        callback(ERRORS.NOT_CONNECTED);
        return;
    }

    if (connector.payments.isEmailRequired && !options.email) {
        callback(ERRORS.PURCHASE.EMAIL_REQUIRED);
        return;
    }

    const player = this.purchasePlayer = new ConnectorActionPlayer([
        function (f, stop) {
            if (connector.payments.isLoginRequired) {
                connector._socialManager.enforceLogin((code) => {
                    if (code) {
                        stop(code);
                    } else {
                        f();
                    }
                });
            } else {
                f();
            }
        },

        function (f, stop) {
            console.log("PaymentsManager purchase start", product.productId, product.itemId);

            pendingEntry = this.savePendingPurchaseContext(product.productId, options.context);

            connector.payments.getPlugin().setPurchaseState(PaymentsManager.STATE_PURCHASE);

            // the store's dialog gets a ceiling: the web stores poll our server with their own 30-minute limit, the others had none
            const storeTimeout = connector.payments.getPlugin() instanceof RestPayments ? undefined
                : connector.timeouts.setTimeout(() => stop(ERRORS.PURCHASE.TIMEOUT, { error: "store_timeout" }), PaymentsManager.STORE_TIMEOUT);

            connector.payments.getPlugin().purchase(product, connector.utils.once((code, receivedPurchase, raw) => {
                if (storeTimeout !== undefined) {
                    connector.timeouts.clearTimeout(storeTimeout);
                }

                purchase = receivedPurchase;

                // the seller's context travels with the purchase to the payment server (payload.context), a restore gets it from the pending entry
                if (purchase && pendingEntry.context) {
                    purchase.context = pendingEntry.context;
                }

                console.log("PaymentsManager purchase result", product.productId, product.itemId, code);
                console.log("PaymentsManager purchase", JSON.stringify(purchase));

                this.attachPendingPaymentId(pendingEntry, purchase);

                if (options.testRestore) {
                    code = ERRORS.PURCHASE.UNKNOWN;
                }

                // the store answered after the purchase was closed (the player closed the overlay, a timeout): the answer is not
                // dropped - it is logged, and a late success is delivered by the restore that follows
                if (!player.isRunning()) {
                    let outcome = "error";
                    if (code === connector.CODE_SUCCEED) {
                        outcome = "success";
                    } else if (code === ERRORS.PURCHASE.CANCEL) {
                        outcome = "cancel";
                    }
                    let error = outcome === "error" ? String(code) : undefined;
                    if (raw && raw.error !== undefined) {
                        error = String(raw.error);
                    }
                    connector.metrics.logEvent(connector.EVENTS.STATS.PAYMENTS_LATE + outcome, {
                        productId: product.itemId,
                        textPrice: product.textPrice,
                        method: connector.payments.getPlugin().pluginName,
                        error,
                        details: {
                            raw: raw && raw.message ? connector.utils.stringify(raw.message).substring(0, 200) : undefined,
                            paymentId: purchase && purchase.paymentId
                        }
                    });

                    if (code === connector.CODE_SUCCEED) {
                        this.restoreLatePurchase(purchase);
                    }
                    return;
                }

                if (code !== connector.CODE_SUCCEED) {
                    stop(code, raw);
                    return;
                }

                if (this.isConsumed(purchase.paymentId)) {
                    if (connector.platform.oneOf(connector.AMAZON) && connector.config.debugMode) {
                        const plugin = connector.payments.getPlugin();
                        plugin.bus.trigger("stream:console", `AMAZON_PAYMENTS Error: purchase already consumed - ${purchase.paymentId}`);
                    }

                    this.consume(purchase);
                    stop(ERRORS.PURCHASE.UNKNOWN, { error: "consumed" });
                    return;
                }

                f();
            }), {
                email: options.email
            });
        }.bind(this),

        function (f, stop) {
            connector.payments.getPlugin().setPurchaseState(PaymentsManager.STATE_VALIDATE);

            console.log("PaymentsManager validate start", product.productId, product.itemId);

            this.validateWithRetries(purchase, (code) => {
                console.log("PaymentsManager validate result", product.productId, product.itemId, code);

                this.attachPendingPaymentId(pendingEntry, purchase);

                if (code === connector.CODE_SUCCEED && !player.isRunning()) {
                    connector.metrics.logEvent(`${connector.EVENTS.STATS.PAYMENTS_LATE}success`, {
                        productId: product.itemId,
                        details: { paymentId: purchase.paymentId, stage: PaymentsManager.STATE_VALIDATE }
                    });
                    this.restoreLatePurchase(purchase);
                    return;
                }

                if (code !== connector.CODE_SUCCEED) {
                    if (code === connector.CODE_FAILED) {
                        this.consume(purchase);
                    }
                    // the store has the purchase, our server has not confirmed it yet: a later restore delivers it
                    stop(code === connector.CODE_CANCELLED ? ERRORS.PURCHASE.PENDING : code);
                    return;
                }

                f();
            });
        }.bind(this)
    ]);

    player.onComplete(() => {
        connector.payments.getPlugin().setPurchaseState(undefined);
    });

    player.onSuccess(() => {
        callback(connector.CODE_SUCCEED, {
            purchase,
            product
        });
    });

    player.onFailure((code, raw) => {
        code = code || ERRORS.PURCHASE.UNKNOWN;

        callback(code, undefined, raw);
    });

    player.play();
};

// a transient validate failure (CODE_CANCELLED: our server unreachable, a 5xx) is retried with backoff for up to VALIDATE_TIMEOUT
// before the purchase is left to the restore; the web stores poll inside their own validate already
PaymentsManager.prototype.validateWithRetries = function (purchase, callback) {
    const plugin = connector.payments.getPlugin();
    const started = Date.now();
    let attempt = 0;

    const tryValidate = () => {
        plugin.validate(purchase, (code) => {
            const transient = code === connector.CODE_CANCELLED && !(plugin instanceof RestPayments);
            if (!transient || Date.now() - started > PaymentsManager.VALIDATE_TIMEOUT) {
                callback(code);
                return;
            }

            const delay = Math.min(PaymentsManager.VALIDATE_INTERVAL * Math.pow(PaymentsManager.VALIDATE_BACKOFF, attempt), PaymentsManager.VALIDATE_MAX_INTERVAL);
            attempt++;
            console.log("PaymentsManager validate retry", purchase.productId, attempt, Math.round(delay));
            connector.timeouts.setTimeout(tryValidate, delay);
        });
    };

    tryValidate();
};

PaymentsManager.STORE_TIMEOUT = connector.utils.parseInterval("3 minutes");
PaymentsManager.CONSUME_TIMEOUT = connector.utils.parseInterval("3 minutes");
PaymentsManager.VALIDATE_TIMEOUT = connector.utils.parseInterval("3 minutes");
PaymentsManager.VALIDATE_INTERVAL = connector.utils.parseInterval("2 seconds");
PaymentsManager.VALIDATE_MAX_INTERVAL = connector.utils.parseInterval("1 minute");
PaymentsManager.VALIDATE_BACKOFF = 1.4;

PaymentsManager.prototype.isPurchaseRunning = function () {
    return this.purchasePlayer && this.purchasePlayer.isRunning();
};

PaymentsManager.prototype.isRestoreRunning = function () {
    return this.restorePlayer && this.restorePlayer.isRunning();
};

PaymentsManager.prototype.isSubscriptionsAvailable = function () {
    if (connector.platform.oneOf(connector.ANDROID) && connector.payments.oneOf(connector.YOOKASSA, connector.RUSTORE)) {
        return false;
    }

    const plugin = connector.payments.getPlugin();
    if (plugin.isSubscriptionsAvailable) {
        return plugin.isSubscriptionsAvailable();
    }

    return connector.platform.oneOf(connector.OK, connector.ANDROID, connector.IOS, connector.MACOS, connector.TEST);
};

PaymentsManager.prototype.stopPurchasePlayer = function (raw) {
    if (this.purchasePlayer) {
        this.purchasePlayer.stop(ERRORS.PURCHASE.UNKNOWN, raw);
        delete this.purchasePlayer;
    }
};

PaymentsManager.prototype.stopLoadProductsPlayer = function () {
    if (this.loadProductsPlayer) {
        this.loadProductsPlayer.stop();
        delete this.loadProductsPlayer;
    }
};

// the products are asked for every two minutes while they are missing: the interval stops at the first load and is armed
// again when a load fails, the plugin changes or the store reconnects (the plugin forgets its products on disconnect)
PaymentsManager.prototype.armLoadProducts = function () {
    if (!this.loadProductsInterval) {
        this.loadProductsInterval = connector.timeouts.setInterval(() => this.tryLoadProductsThrottled(), connector.utils.parseInterval("2 minutes"));
    }
};

PaymentsManager.prototype.disarmLoadProducts = function () {
    if (this.loadProductsInterval) {
        connector.timeouts.clearInterval(this.loadProductsInterval);
        delete this.loadProductsInterval;
    }
};

PaymentsManager.prototype.tryLoadProducts = function () {
    console.log("[connector] Try load products");

    if (!connector.payments.isConnected() || this.isPageBusy()) {
        return;
    }

    if (this.loadProductsPlayer && this.loadProductsPlayer.isRunning()) {
        return;
    }

    const player = this.loadProductsPlayer = new ConnectorActionPlayer({
        actionTimeout: connector.utils.parseInterval("60 seconds"),
        actions: [
            (f, stop) => {
                console.log("[connector] Start load products");

                connector.payments.getPlugin().loadProducts((code) => {
                    console.log("[connector] Load products:", code);

                    if (code === connector.CODE_SUCCEED) {
                        f();
                    } else {
                        stop();
                    }
                });
            },

            (f, stop) => {
                connector.payments.getPlugin().loadSubscriptions((code) => {
                    if (code === connector.CODE_SUCCEED) {
                        this.restoreThrottled();

                        f();
                    } else {
                        stop();
                    }
                });
            },

            function (f, stop) {
                connector.payments.getPlugin().loadSubscriptionsTokens((code) => {
                    if (code === connector.CODE_SUCCEED) {
                        f();
                    } else {
                        stop();
                    }
                });
            }
        ]
    });

    player.onSuccess(() => {
        this.disarmLoadProducts();
    });

    player.onFailure(() => {
        const plugin = connector.payments.getPlugin();
        plugin.bus.trigger("failedTryLoadProducts");
    });

    player.play();
};

PaymentsManager.STATE_PURCHASE = "purchase";
PaymentsManager.STATE_VALIDATE = "validate";

PaymentsManager.PENDING_STORAGE_LIMIT = 30;
// a restore comes on the next launch: 3.5% of restores (Android 11%) come more than 7 days after the last purchase open, 1.6% after 30 days
PaymentsManager.PENDING_LIFETIME = connector.utils.parseInterval("30 days");
PaymentsManager.CONSUMED_STORAGE_LIMIT = 30;
PaymentsManager.LOGGED_STORAGE_LIMIT = 30;
